import React, { ReactNode, useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import stylesPc from "./Layout.module.css";
import stylesSp from "./Layout_SP.module.css";
import { IsDevice } from "../../../components/function/useDeviceFlag";
import Header from "./header/Header";
import Footer from "./footer/Footer";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../RootReducer";
import { LoginStarted } from "../../../user/UserActions";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import { useAccount, useMsal } from "@azure/msal-react";
import { SnackbarProvider } from "notistack";
import { useHistory, useLocation } from "react-router-dom";
import classNames from "classnames";
import favicon from "../../../assets/image/favicon.png";
import { isAdmin, isEditor, isUnavailable } from "../../../utils/LogicUtils";
import jwt from "jwt-decode";
import { t } from "i18next";
import i18n from "../../../i18n";

const DEFAULT_FAVICONS = [
    { rel: "apple-touch-icon", sizes: "180x180", href: favicon },
    { rel: "icon", type: "image/png", sizes: "32x32", href: favicon },
    { rel: "icon", type: "image/png", sizes: "16x16", href: favicon },
    { rel: "icon", type: "image/png", href: favicon },
];

const faviconLinks = DEFAULT_FAVICONS;

function Layout({ children }: { children: ReactNode }) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : stylesSp;

    const {
        userInfo,
        isSendVideo,
        isSuccessSendVideo,
        isFailedSendVideo,
        isEditVideo,
        isSuccessEditVideo,
        isFailedEditVideo,
        isUploadDocument,
        isSuccessUploadDocument,
        isFailedUploadDocument,
        isDeleteVideo,
        isSuccessDeleteVideo,
        isFailedDeleteVideo,
        isUpdateRecVideos,
        isUpdateSuccessRecVideos,
        isUpdateFailedRecVideos,
        isUpdateRecPrograms,
        isUpdateSuccessRecPrograms,
        isUpdateFailedRecPrograms,
        isUpdateRecProfessionals,
        isUpdateSuccessRecProfessionals,
        isUpdateFailedRecProfessionals,
        isUpdateUserInfo,
        isSuccessUpdateUserInfo,
        isFailedUpdateUserInfo,
        isEditSTT,
        isSuccessEditSTT,
        isFailedEditSTT,
        isRerunSTT,
        isSuccessRerunSTT,
        isEditSlide,
        isSuccessEditSlide,
        isFailedEditSlide,
        isCreateNewSlideComplete,
        isFailedRerunSTT,
        isSuccessGetTempFilePageCount,
        isLoadingGetTempFilePageCount,
        isDeleteSlide,
        isSuccessDeleteSlide,
        isFailedDeleteSlide,
        loadingTimeline,
        isSuccessUploadTimeline,
        isFailedUploadTimeline,
        isFailedLogin,
        isSessionTimeout,
        fullName,
    } = useSelector((state: AppState) => ({
        user: state.user.user,
        userInfo: state.user.userInfo,
        loadingUser: state.user.isLoading,
        isSendVideo: state.video.isSendVideo,
        isSuccessSendVideo: state.video.isSuccessSendVideo,
        isFailedSendVideo: state.video.isFailedSendVideo,
        isEditVideo: state.video.isEditVideo,
        isSuccessEditVideo: state.video.isSuccessEditVideo,
        isFailedEditVideo: state.video.isFailedEditVideo,
        isUploadDocument: state.video.isUploadDocument,
        isSuccessUploadDocument: state.video.isSuccessUploadDocument,
        isFailedUploadDocument: state.video.isFailedUploadDocument,
        isDeleteVideo: state.video.isDeleteVideo,
        isSuccessDeleteVideo: state.video.isSuccessDeleteVideo,
        isFailedDeleteVideo: state.video.isFailedDeleteVideo,
        isUpdateRecVideos: state.recommendList.isUpdateRecVideos,
        isUpdateSuccessRecVideos: state.recommendList.isUpdateSuccessRecVideos,
        isUpdateFailedRecVideos: state.recommendList.isUpdateFailedRecVideos,
        isUpdateRecPrograms: state.recommendList.isUpdateRecPrograms,
        isUpdateSuccessRecPrograms: state.recommendList.isUpdateSuccessRecPrograms,
        isUpdateFailedRecPrograms: state.recommendList.isUpdateFailedRecPrograms,
        isUpdateRecProfessionals: state.recommendList.isUpdateRecProfessionals,
        isUpdateSuccessRecProfessionals: state.recommendList.isUpdateSuccessRecProfessionals,
        isUpdateFailedRecProfessionals: state.recommendList.isUpdateFailedRecProfessionals,
        isUpdateUserInfo: state.user.isUpdateUserInfo,
        isSuccessUpdateUserInfo: state.user.isSuccessUpdateUserInfo,
        isFailedUpdateUserInfo: state.user.isFailedUpdateUserInfo,
        isEditSTT: state.exVideo.caption.isEditSTT,
        isSuccessEditSTT: state.exVideo.caption.isSuccessEditSTT,
        isFailedEditSTT: state.exVideo.caption.isFailedEditSTT,
        isRerunSTT: state.exVideo.caption.isRerunSTT,
        isSuccessRerunSTT: state.exVideo.caption.isSuccessRerunSTT,
        isFailedRerunSTT: state.exVideo.caption.isFailedRerunSTT,
        isSuccessGetTempFilePageCount: state.slide.isSuccessGetTempFilePageCount,
        isLoadingGetTempFilePageCount: state.slide.isLoadingGetTempFilePageCount,
        isEditSlide: state.slide.isEditSlide,
        isSuccessEditSlide: state.slide.isSuccessEditSlide,
        isFailedEditSlide: state.slide.isFailedEditSlide,
        isCreateNewSlideComplete: state.slide.isCreateNewSlideComplete,
        isDeleteSlide: state.slide.isDeleteSlide,
        isSuccessDeleteSlide: state.slide.isSuccessDeleteSlide,
        isFailedDeleteSlide: state.slide.isFailedDeleteSlide,
        loadingTimeline: state.timeline.isLoadingUploadTimeline,
        isSuccessUploadTimeline: state.timeline.isSuccessUploadTimeline,
        isFailedUploadTimeline: state.timeline.isFailedUploadTimeline,
        isFailedLogin: state.user.isFailedLogin,
        isSessionTimeout: state.user.isSessionTimeout,
        fullName: state.user.userInfo?.fullName,
    }));

    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();

    // アカウント情報セット
    const { accounts, instance } = useMsal();
    const account: any = useAccount(accounts[0]);

    const checkSessionTimeout = () => {
        // セクションタイムアウト
        if (pathname !== "/sessionTimeout") {
            if (localStorage.getItem("isSessionTimeout")) {
                history.push("/sessionTimeout");
                return;
            }
        }

        // リフレッシュTokenが切れる場合、セクションタイムアウト画面に移動
        const currentRefreshToken = localStorage.getItem("cineonhub_access_refreshToken");

        const nowInSeconds = Math.round(Date.now() / 1000);
        let refreshTokenDecoded: any = null;
        if (currentRefreshToken) {
            try {
                refreshTokenDecoded = jwt(currentRefreshToken);
            } catch (e) {
                instance.logout();
                return;
            }

            if (refreshTokenDecoded && refreshTokenDecoded.exp < nowInSeconds) {
                if (pathname !== "/sessionTimeout") {
                    history.push("/sessionTimeout");
                }
                return;
            }
        }
    };

    useEffect(() => {
        checkSessionTimeout();
        // eslint-disable-next-line
    }, [history, pathname]);

    // ログインユーザー情報をリクエスト
    useEffect(() => {
        if (!account) return;
        checkSessionTimeout();

        // checkTokenLifeTime();

        let azureSecret = "";
        const item = Object.keys(localStorage);
        const authUrl = "-" + process.env.REACT_APP_AUTH_DOMAIN + "-accesstoken-";
        for (let i = 0; i < item.length; i++) {
            if (item[i].includes(authUrl)) {
                const targetKey = localStorage.getItem(item[i]);
                if (targetKey) {
                    const item2 = JSON.parse(targetKey);
                    azureSecret = item2.secret;
                }
            }
        }

        const loginInfo = {
            token: azureSecret,
            email: account?.idTokenClaims.emails[0],
            fullName:
                fullName ??
                localStorage.getItem("fullName") ??
                account?.idTokenClaims.family_name + " " + account?.idTokenClaims.given_name,
            profileImageUrl: "",
            organization: account?.idTokenClaims.extension_organization,
            localAccountId: account?.localAccountId,
            isNew: account?.idTokenClaims.newUser ? true : false,
        };
        dispatch(LoginStarted(loginInfo));

        const language = window.navigator.language.substring(0, 2) === "ja" ? "ja" : "en";

        i18n.changeLanguage(language);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // ログイン失敗したら、ログアウトさせる
    useEffect(() => {
        if (isFailedLogin) instance.logout();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFailedLogin]);

    // 利用の有効無効判定
    useEffect(() => {
        if (isUnavailable(userInfo?.roles)) {
            history.push("/loginUnavailable");
        }
        // Admin権限とEditor権限以外、studioページにアクセスできないようにする
        if (
            userInfo &&
            !isAdmin(userInfo.roles) &&
            !isEditor(userInfo.roles) &&
            pathname.startsWith("/studio")
        ) {
            history.push("/error");
        }
    }, [userInfo, history, pathname]);

    // const checkTokenLifeTime = () => {
    //     const exp = account?.idTokenClaims.exp
    //     console.log(typeof(exp), exp)
    //     var date = new Date();
    //     var now = Math.floor(date.getTime()/1000)
    //     console.log(typeof(now), now)
    //     if (now > exp) {
    //         console.log("time to logout")
    //         localStorage.clear();
    //         instance.logout();
    //     }
    // }

    // -------------------- 新規ビデオアップロード --------------------
    // バックエンドに送信中フラグ
    const [isStartSendVideo, ChangeStartSendVideoFlag] = useState(false);
    const [isVisibleSuccessSendVideo, ChangeVisibleSuccessSendVideo] = useState(false);
    const [isVisibleFailedSendVideo, ChangeVisibleFailedSendVideo] = useState(false);
    const CloseUploadEndBar = () => {
        ChangeStartSendVideoFlag(false);
        ChangeVisibleSuccessSendVideo(false);
        ChangeVisibleFailedSendVideo(false);
    };

    useEffect(() => {
        if (isSendVideo === true) {
            ChangeStartSendVideoFlag(true);
            ChangeVisibleSuccessSendVideo(false);
            ChangeVisibleFailedSendVideo(false);
        }
        if (isSendVideo === false) {
            if (isStartSendVideo === true) {
                if (isSuccessSendVideo === true) {
                    ChangeVisibleSuccessSendVideo(true);
                } else if (isFailedSendVideo === true) {
                    ChangeVisibleFailedSendVideo(true);
                } else {
                    console.log("visible addVideo Failed");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSendVideo, isSuccessSendVideo, isFailedSendVideo]);

    // -------------------- ビデオ情報データアップデート --------------------
    // バックエンドに送信中フラグ
    const [isStartEditVideo, ChangeStartEditVideoFlag] = useState(false);
    const [isVisibleSuccessEditVideo, ChangeVisibleSuccessEditVideo] = useState(false);
    const [isVisibleFailedEditVideo, ChangeVisibleFailedEditVideo] = useState(false);
    const CloseEditVideoBar = () => {
        ChangeStartEditVideoFlag(false);
        ChangeVisibleSuccessEditVideo(false);
        ChangeVisibleFailedEditVideo(false);
    };

    useEffect(() => {
        if (isEditVideo === true) {
            ChangeStartEditVideoFlag(true);
            ChangeVisibleSuccessEditVideo(false);
            ChangeVisibleFailedEditVideo(false);
        }
        if (isUploadDocument === true) {
            ChangeStartEditVideoFlag(true);
            ChangeVisibleSuccessEditVideo(false);
            ChangeVisibleFailedEditVideo(false);
        }
        if (isEditVideo === false && isUploadDocument === false) {
            if (isStartEditVideo === true) {
                if (isFailedEditVideo === true || isFailedUploadDocument === true) {
                    ChangeVisibleFailedEditVideo(true);
                } else if (isSuccessEditVideo === true || isSuccessUploadDocument === true) {
                    ChangeVisibleSuccessEditVideo(true);
                } else {
                    console.log("visible updateRecommend Failed");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isEditVideo,
        isSuccessEditVideo,
        isFailedEditVideo,
        isUploadDocument,
        isSuccessUploadDocument,
        isFailedUploadDocument,
    ]);

    // -------------------- ビデオ削除 --------------------
    // バックエンドに送信中フラグ
    const [isStartDeleteVideo, ChangeStartDeleteVideoFlag] = useState(false);
    const [isVisibleSuccessDeleteVideo, ChangeVisibleSuccessDeleteVideo] = useState(false);
    const [isVisibleFailedDeleteVideo, ChangeVisibleFailedDeleteVideo] = useState(false);
    const CloseDeleteVideoBar = () => {
        ChangeStartDeleteVideoFlag(false);
        ChangeVisibleSuccessDeleteVideo(false);
        ChangeVisibleFailedDeleteVideo(false);
    };

    useEffect(() => {
        if (isDeleteVideo === true) {
            ChangeStartDeleteVideoFlag(true);
            ChangeVisibleSuccessDeleteVideo(false);
            ChangeVisibleFailedDeleteVideo(false);
        }
        if (isDeleteVideo === false) {
            if (isStartDeleteVideo === true) {
                if (isSuccessDeleteVideo === true) {
                    ChangeVisibleSuccessDeleteVideo(true);
                } else if (isFailedDeleteVideo === true) {
                    ChangeVisibleFailedDeleteVideo(true);
                } else {
                    console.log("visible deleteVideo Failed");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDeleteVideo, isSuccessDeleteVideo, isFailedDeleteVideo]);

    // -------------------- コンテンツ情報データアップデート --------------------
    // バックエンドに送信中フラグ
    const [isStartEditSlide, ChangeStartEditSlideFlag] = useState(false);
    const [isVisibleSuccessEditSlide, ChangeVisibleSuccessEditSlide] = useState(false);
    const [isVisibleFailedEditSlide, ChangeVisibleFailedEditSlide] = useState(false);
    const CloseEditSlideBar = () => {
        ChangeStartEditSlideFlag(false);
        ChangeVisibleSuccessEditSlide(false);
        ChangeVisibleFailedEditSlide(false);
    };

    useEffect(() => {
        if (isEditSlide === true) {
            ChangeStartEditSlideFlag(true);
            ChangeVisibleSuccessEditSlide(false);
            ChangeVisibleFailedEditSlide(false);
        } else {
            if (isStartEditSlide === true) {
                if (isFailedEditSlide === true) {
                    ChangeVisibleFailedEditSlide(true);
                } else if (isSuccessEditSlide === true) {
                    ChangeVisibleSuccessEditSlide(true);
                } else {
                    console.log("Failed to display editSlide message.");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditSlide, isSuccessEditSlide, isFailedEditSlide]);

    // -------------------- コンテンツ削除 --------------------
    // バックエンドに送信中フラグ
    const [isStartDeleteSlide, ChangeStartDeleteSlideFlag] = useState(false);
    const [isVisibleSuccessDeleteSlide, ChangeVisibleSuccessDeleteSlide] = useState(false);
    const [isVisibleFailedDeleteSlide, ChangeVisibleFailedDeleteSlide] = useState(false);
    const CloseDeleteSlideBar = () => {
        ChangeStartDeleteSlideFlag(false);
        ChangeVisibleSuccessDeleteSlide(false);
        ChangeVisibleFailedDeleteSlide(false);
    };

    useEffect(() => {
        if (isDeleteSlide === true) {
            ChangeStartDeleteSlideFlag(true);
            ChangeVisibleSuccessDeleteSlide(false);
            ChangeVisibleFailedDeleteSlide(false);
        }
        if (isDeleteSlide === false) {
            if (isStartDeleteSlide === true) {
                if (isSuccessDeleteSlide === true) {
                    ChangeVisibleSuccessDeleteSlide(true);
                } else if (isFailedDeleteSlide === true) {
                    ChangeVisibleFailedDeleteSlide(true);
                } else {
                    console.log("Failed to display deleteSlide message.");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDeleteSlide, isSuccessDeleteSlide, isFailedDeleteSlide]);

    // -------------------- おすすめ管理データアップデート --------------------
    // バックエンドに送信中フラグ
    const [isStartUpdateRecommend, ChangeStartRecommendFlag] = useState(false);
    const [isVisibleSuccessRecommend, ChangeVisibleSuccessRecommend] = useState(false);
    const [isVisibleFailedRecommend, ChangeVisibleFailedRecommend] = useState(false);
    const CloseUpdateRecommendBar = () => {
        ChangeVisibleSuccessRecommend(false);
        ChangeVisibleFailedRecommend(false);
        ChangeStartRecommendFlag(false);
    };

    useEffect(() => {
        if (
            isUpdateRecVideos === true ||
            isUpdateRecPrograms === true ||
            isUpdateRecProfessionals === true
        ) {
            ChangeStartRecommendFlag(true);
            ChangeVisibleSuccessRecommend(false);
            ChangeVisibleFailedRecommend(false);
        }
        if (
            isUpdateRecVideos === false &&
            isUpdateRecPrograms === false &&
            isUpdateRecProfessionals === false
        ) {
            if (isStartUpdateRecommend === true) {
                if (
                    isUpdateFailedRecVideos === true ||
                    isUpdateFailedRecPrograms === true ||
                    isUpdateFailedRecProfessionals === true
                ) {
                    ChangeVisibleFailedRecommend(true);
                } else if (
                    isUpdateSuccessRecVideos === true ||
                    isUpdateSuccessRecPrograms === true ||
                    isUpdateSuccessRecProfessionals === true
                ) {
                    ChangeVisibleSuccessRecommend(true);
                } else {
                    console.log("visible updateRecommend Failed");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUpdateRecVideos, isUpdateRecPrograms, isUpdateRecProfessionals]);

    // -------------------- ユーザー管理データアップデート --------------------
    // バックエンドに送信中フラグ
    const [isStartUpdateUsers, ChangeStartUsersFlag] = useState(false);
    const [isVisibleUpdateUsers, ChangeVisibleUpdateUsers] = useState(false);
    const [isVisibleSuccessUsers, ChangeVisibleSuccessUsers] = useState(false);
    const [isVisibleFailedUsers, ChangeVisibleFailedUsers] = useState(false);
    const CloseUpdateUsersBar = () => {
        ChangeVisibleUpdateUsers(false);
        ChangeStartUsersFlag(false);
    };

    const CloseUpdateEndUsersBar = () => {
        ChangeVisibleSuccessUsers(false);
        ChangeVisibleFailedUsers(false);
        ChangeStartUsersFlag(false);
    };

    useEffect(() => {
        if (isUpdateUserInfo === true) {
            ChangeVisibleUpdateUsers(true);
            ChangeStartUsersFlag(true);
            ChangeVisibleSuccessUsers(false);
            ChangeVisibleFailedUsers(false);
        }
        if (isUpdateUserInfo === false) {
            CloseUpdateUsersBar();
            if (isStartUpdateUsers === true) {
                if (isSuccessUpdateUserInfo === true) {
                    ChangeVisibleSuccessUsers(true);
                } else if (isFailedUpdateUserInfo === true) {
                    ChangeVisibleFailedUsers(true);
                } else {
                    console.log("visible updateUserInfo Failed");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUpdateUserInfo, isSuccessUpdateUserInfo, isFailedUpdateUserInfo]);

    // -------------------- ビデオSTT編集 --------------------
    // バックエンドに送信中フラグ
    const [isStartEditSTT, ChangeStartEditSTTFlag] = useState(false);
    const [isVisibleSuccessEditSTT, ChangeVisibleSuccessEditSTT] = useState(false);
    const [isVisibleFailedEditSTT, ChangeVisibleFailedEditSTT] = useState(false);

    const CloseEditSTTEndBar = () => {
        ChangeStartEditSTTFlag(false);
        ChangeVisibleSuccessEditSTT(false);
        ChangeVisibleFailedEditSTT(false);
    };

    useEffect(() => {
        if (isEditSTT === true) {
            ChangeStartEditSTTFlag(true);
            ChangeVisibleSuccessEditSTT(false);
            ChangeVisibleFailedEditSTT(false);
        } else {
            if (isStartEditSTT === true) {
                if (isSuccessEditSTT === true) {
                    ChangeVisibleSuccessEditSTT(true);
                } else if (isFailedEditSTT === true) {
                    ChangeVisibleFailedEditSTT(true);
                } else {
                    console.log("visible EditSTT Failed");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditSTT, isSuccessEditSTT, isFailedEditSTT]);

    // -------------------- ビデオSTT再実行 --------------------
    // バックエンドに送信中フラグ
    const [isStartRerunSTT, ChangeStartRerunSTTFlag] = useState(false);
    const [isExtractSTTStart, setIsExtractSTTStart] = useState(false);

    const CloseExtarctSTTMsg = () => {
        setIsExtractSTTStart(false);
    };

    useEffect(() => {
        if (isRerunSTT === true) {
            ChangeStartRerunSTTFlag(true);
            setIsExtractSTTStart(true);
        } else {
            if (isStartRerunSTT === true) {
                if (isSuccessRerunSTT === true) {
                    setIsExtractSTTStart(false);
                } else if (isFailedRerunSTT === true) {
                    setIsExtractSTTStart(false);
                } else {
                    console.log("visible RerunSTT Failed");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRerunSTT, isSuccessRerunSTT, isFailedRerunSTT]);

    // -------------------- cineonhub、cineonstudioのメニュー切り替え --------------------
    const [isVisibleSwitchService, ChangeVisibleSwitchService] = useState(false);
    const CloseSwitchServiceBar = () => {
        ChangeVisibleSwitchService(false);
    };

    // 使っていないので、一旦コメントアウトする
    // const HandleScroll = useCallback(() => {
    //     const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    //     dispatch(onScroll(scrollTop));
    // }, [dispatch]);

    // useEffect(() => {
    //     document.addEventListener("scroll", HandleScroll, { passive: true });
    //     return () => {
    //         document.removeEventListener("scroll", HandleScroll);
    //     };
    // }, [HandleScroll]);

    // ----------------------timelineアップロード---------------------------------
    const [isStartSendTimeline, ChangeStartSendTimelineFlag] = useState(false);
    const [isVisibleSuccessSendTimeline, ChangeVisibleSuccessSendTimeline] = useState(false);
    const [isVisibleFailedSendTimeline, ChangeVisibleFailedSendTimeline] = useState(false);
    const CloseUploadTimelineEndBar = () => {
        ChangeStartSendTimelineFlag(false);
        ChangeVisibleSuccessSendTimeline(false);
        ChangeVisibleFailedSendTimeline(false);
    };

    useEffect(() => {
        if (loadingTimeline === true) {
            ChangeStartSendTimelineFlag(true);
            ChangeVisibleSuccessSendTimeline(false);
            ChangeVisibleFailedSendTimeline(false);
        }
        if (loadingTimeline === false) {
            if (isStartSendTimeline === true) {
                if (isSuccessUploadTimeline === true) {
                    ChangeVisibleSuccessSendTimeline(true);
                } else if (isFailedUploadTimeline === true) {
                    ChangeVisibleFailedSendTimeline(true);
                } else {
                    console.log("visible addTimeline Failed");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingTimeline, isSuccessUploadTimeline, isFailedUploadTimeline]);

    // ----------------------slidestockアップロード---------------------------------
    const [isVisibleSlideMsg, setIsVisibleSlideMsg] = useState(false);
    const [isVisibleCountSlideMsg, setIsVisibleCountSlideMsg] = useState(false);
    const [isVisibleFailedSendSlide, setIsVisibleFailedSendSlide] = useState(false);

    const CloseUploadSendSlide = () => {
        setIsVisibleSlideMsg(false);
        setIsVisibleCountSlideMsg(false);
        setIsVisibleFailedSendSlide(false);
    };

    useEffect(() => {
        if (isSuccessGetTempFilePageCount === true) {
            if (isLoadingGetTempFilePageCount === false) {
                setIsVisibleCountSlideMsg(true);
            }
            if (isCreateNewSlideComplete === true) {
                setIsVisibleSlideMsg(true);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingGetTempFilePageCount, isCreateNewSlideComplete]);

    return (
        <div
            className={classNames(styles.root, {
                [styles.homeLayout]: pathname.startsWith("/homepage"),
                [styles.marginTopNotHome]: !pathname.startsWith("/homepage"),
                [styles.overflowUnset]: pathname.startsWith("/channel"),
                [styles.isEditTimeline]: pathname.startsWith("/studio/editTimeline"),
            })}
        >
            {
                <HelmetProvider>
                    <Helmet link={faviconLinks}>
                        <title>cineonhub</title>
                    </Helmet>
                </HelmetProvider>
            }

            {/* ビデオアップロード */}
            <>
                <Snackbar open={isSendVideo}>
                    <Alert icon={false} severity="success">
                        {t("Videoのアップロード準備中です。ブラウザ画面を更新しないでください")}
                    </Alert>
                </Snackbar>
                <Snackbar
                    autoHideDuration={5000}
                    open={isVisibleSuccessSendVideo}
                    onClose={CloseUploadEndBar}
                >
                    <Alert severity="success">
                        <div>
                            {t(
                                "Videoのアップロード準備が完了しました。続けてVideoをアップロードできます"
                            )}
                        </div>
                        <div>
                            {IsDevice("desktop")
                                ? t(
                                      "現在のアップロード状況は管理画面の「進捗管理」で確認できます。Videoサイズによっては反映に時間がかかる場合があります"
                                  )
                                : t(
                                      "現在のアップロード状況はPC上の管理画面の「進捗管理」で確認できます。Videoサイズによっては反映に時間がかかる場合があります"
                                  )}
                        </div>
                    </Alert>
                </Snackbar>
                <Snackbar open={isVisibleFailedSendVideo} onClose={CloseUploadEndBar}>
                    <Alert severity="error" onClose={CloseUploadEndBar}>
                        {t("Videoアップロードに失敗しました")}
                    </Alert>
                </Snackbar>
            </>

            {/* ビデオ情報アップデート */}
            <>
                <Snackbar open={isEditVideo}>
                    <Alert icon={false} severity="success" onClose={CloseEditVideoBar}>
                        {t("Videoを更新しています。ブラウザ画面を更新しないでください")}
                    </Alert>
                </Snackbar>
                <Snackbar open={isVisibleSuccessEditVideo} onClose={CloseEditVideoBar}>
                    <Alert severity="success" onClose={CloseEditVideoBar}>
                        {t("Videoを更新しました")}
                    </Alert>
                </Snackbar>
                <Snackbar open={isVisibleFailedEditVideo} onClose={CloseEditVideoBar}>
                    <Alert severity="error" onClose={CloseEditVideoBar}>
                        {t("Videoの更新に失敗しました")}
                    </Alert>
                </Snackbar>
            </>

            {/* ビデオ削除 */}
            <>
                <Snackbar
                    autoHideDuration={3000}
                    open={isVisibleSuccessDeleteVideo}
                    onClose={CloseDeleteVideoBar}
                >
                    <Alert severity="success" onClose={CloseDeleteVideoBar}>
                        {t("Videoを削除しました")}
                    </Alert>
                </Snackbar>
                <Snackbar
                    autoHideDuration={3000}
                    open={isVisibleFailedDeleteVideo}
                    onClose={CloseDeleteVideoBar}
                >
                    <Alert severity="error" onClose={CloseDeleteVideoBar}>
                        {t("Videoの削除に失敗しました")}
                    </Alert>
                </Snackbar>
            </>

            {/* コンテンツ情報アップデート */}
            <>
                <Snackbar open={isEditSlide}>
                    <Alert icon={false} severity="success">
                        {t("Slideを更新しています。ブラウザ画面を更新しないでください")}
                    </Alert>
                </Snackbar>
                <Snackbar open={isVisibleSuccessEditSlide} onClose={CloseEditSlideBar}>
                    <Alert severity="success" onClose={CloseEditSlideBar}>
                        {t("Slideを更新しました")}
                    </Alert>
                </Snackbar>
                <Snackbar open={isVisibleFailedEditSlide} onClose={CloseEditSlideBar}>
                    <Alert severity="error" onClose={CloseEditSlideBar}>
                        {t("Slideの更新に失敗しました")}
                    </Alert>
                </Snackbar>
            </>

            {/* コンテンツ削除 */}
            <>
                <Snackbar
                    autoHideDuration={3000}
                    open={isVisibleSuccessDeleteSlide}
                    onClose={CloseDeleteSlideBar}
                >
                    <Alert severity="success">{t("Slideを削除しました")}</Alert>
                </Snackbar>
                <Snackbar open={isVisibleFailedDeleteSlide} onClose={CloseDeleteSlideBar}>
                    <Alert severity="error" onClose={CloseDeleteSlideBar}>
                        {t("Slideの削除に失敗しました")}
                    </Alert>
                </Snackbar>
            </>

            {/* おすすめ管理アップデート */}
            <>
                <Snackbar
                    autoHideDuration={3000}
                    open={isVisibleSuccessRecommend}
                    onClose={CloseUpdateRecommendBar}
                >
                    <Alert severity="success"> {t("おすすめを更新しました")}</Alert>
                </Snackbar>
                <Snackbar open={isVisibleFailedRecommend} onClose={CloseUpdateRecommendBar}>
                    <Alert severity="error" onClose={CloseUpdateRecommendBar}>
                        {t("おすすめの更新に失敗しました")}
                    </Alert>
                </Snackbar>
            </>

            {/* ユーザ情報アップデート */}
            <>
                <Snackbar open={isVisibleUpdateUsers} onClose={CloseUpdateUsersBar}>
                    <Alert icon={false} severity="success">
                        {t("Userを更新しています。ブラウザ画面を更新しないでください")}
                    </Alert>
                </Snackbar>
                <Snackbar
                    autoHideDuration={3000}
                    open={isVisibleSuccessUsers}
                    onClose={CloseUpdateEndUsersBar}
                >
                    <Alert severity="success"> {t("Userを更新しました")}</Alert>
                </Snackbar>
                <Snackbar open={isVisibleFailedUsers} onClose={CloseUpdateEndUsersBar}>
                    <Alert severity="error" onClose={CloseUpdateEndUsersBar}>
                        {t("Userの更新に失敗しました")}
                    </Alert>
                </Snackbar>
            </>

            {/* ビデオSTT再実行 */}
            <>
                <Snackbar
                    autoHideDuration={3000}
                    open={isVisibleSuccessEditSTT}
                    onClose={CloseEditSTTEndBar}
                >
                    <Alert severity="success">
                        <div> {t("STTを更新しました")}</div>
                    </Alert>
                </Snackbar>
                <Snackbar open={isVisibleFailedEditSTT} onClose={CloseEditSTTEndBar}>
                    <Alert severity="error" onClose={CloseEditSTTEndBar}>
                        {t("STTの更新に失敗しました")}
                    </Alert>
                </Snackbar>
            </>

            {/* ビデオSTT編集 */}
            <>
                <Snackbar
                    autoHideDuration={3000}
                    open={isExtractSTTStart}
                    onClose={CloseExtarctSTTMsg}
                >
                    <Alert severity="success" onClose={CloseExtarctSTTMsg}>
                        <div> {t("STTを再実行中です")}</div>
                        <div> {t("アップロード状況は管理画面の「進捗管理」で確認できます")}</div>
                    </Alert>
                </Snackbar>
            </>

            {/* timelineアップロード */}
            <>
                <Snackbar open={loadingTimeline}>
                    <Alert icon={false} severity="success">
                        {t("Timelineをアップロードしています。ブラウザ画面を更新しないでください")}
                    </Alert>
                </Snackbar>
                <Snackbar
                    autoHideDuration={5000}
                    open={isVisibleSuccessSendTimeline}
                    onClose={CloseUploadTimelineEndBar}
                >
                    <Alert severity="success">
                        <div> {t("Timelineのアップロードが完了しました")}</div>
                    </Alert>
                </Snackbar>
                <Snackbar open={isVisibleFailedSendTimeline} onClose={CloseUploadTimelineEndBar}>
                    <Alert severity="error" onClose={CloseUploadTimelineEndBar}>
                        {t("Timelineのアップロードに失敗しました")}
                    </Alert>
                </Snackbar>
            </>
            {/* cineonhub、cineonstudioのメニュー切り替え */}
            <>
                <Snackbar
                    autoHideDuration={2000}
                    open={isVisibleSwitchService}
                    onClose={CloseSwitchServiceBar}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    className={styles.anchorOriginTopRight}
                >
                    <>
                        <Alert severity="info" onClose={CloseSwitchServiceBar}>
                            {pathname.startsWith("/studio/") ? "cineonstudio" : "cineonhub"}
                            &nbsp; {t("に移動しました")}
                        </Alert>
                    </>
                </Snackbar>
            </>
            {/* ファイル読み込み */}
            <>
                <Snackbar open={isLoadingGetTempFilePageCount}>
                    <Alert icon={false} severity="success">
                        {t("ファイルをアップロード準備中です。ブラウザ画面を更新しないでください")}
                    </Alert>
                </Snackbar>
                <Snackbar
                    autoHideDuration={2000}
                    open={isVisibleCountSlideMsg}
                    onClose={CloseUploadSendSlide}
                >
                    <Alert severity="success">
                        <div> {t("ファイルのアップロード準備が完了しました")}</div>
                    </Alert>
                </Snackbar>
            </>
            {/* 新規コンテンツ作成 */}
            <>
                <Snackbar
                    autoHideDuration={2000}
                    open={isVisibleSlideMsg}
                    onClose={CloseUploadSendSlide}
                >
                    <Alert severity="success">
                        <div> {t("ファイルのアップロードが完了しました")}</div>
                    </Alert>
                </Snackbar>
                <Snackbar open={isVisibleFailedSendSlide} onClose={CloseUploadTimelineEndBar}>
                    <Alert severity="error" onClose={CloseUploadTimelineEndBar}>
                        {t("ファイルのアップロードに失敗しました")}
                    </Alert>
                </Snackbar>
            </>

            <>
                {userInfo && !isUnavailable(userInfo?.roles) && !isSessionTimeout && <Header />}
                <div
                    className={classNames(styles.content, {
                        [styles.marginTopSlide]: !pathname.startsWith("/homepage"),
                    })}
                >
                    <div className={styles.upperSlide}>
                        <div className={styles.contentBody}>
                            <SnackbarProvider
                                maxSnack={3}
                                hideIconVariant={false}
                                dense={true}
                                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                            >
                                {children}
                            </SnackbarProvider>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        </div>
    );
}

export default Layout;
